import { getPricePerInstallment } from '@skytech/utils'

import type { AssetDetails, PolicyCoreData } from '@/components/ecom-policy/types'
import { useCart } from '@/contexts/cart/cart-context'
import type { MasterOrderAssetRequest } from '@/resources/ecom-policy-orders'

type MonthlyPriceByInterval = { monthlyPrice: number; paymentInterval: number }

export const useTotalSummary = () => {
  const { getPolicyIds, getNonMandatoryItemsGroupedByPolicy, getPolicyIdsWithoutGreenpool } =
    useCart()

  const countAssetsTotal = (assetsState: MasterOrderAssetRequest[], assets: AssetDetails[]) => {
    const filteredAssetsState = assetsState.filter(item => getPolicyIds().includes(item.policyId))
    const assetTotal = filteredAssetsState.reduce((acc, assetReq) => {
      const asset = assets.find(a => a.id === assetReq.assetId && a.policyId === assetReq.policyId)
      if (asset) {
        let price = 0
        if (assetReq.decision === 'BUYOUT') {
          price = asset.priceBuyout || 0
        } else if (assetReq.decision === 'RETURN') {
          price = asset.priceReturn || 0
        }
        return acc + price
      }
      return acc
    }, 0)
    return assetTotal
  }

  // Return payroll deduction case based on policies and asset total
  // 'default' for selected policies with default intervals (0 or 1)
  // 'varied' for selected policies with varied intervals
  // number (interval) for selected policies with same interval and asset total = 0
  const getPayrollDeductionCase = (policyList: PolicyCoreData[], assetTotal: number) => {
    const policyIds = getPolicyIdsWithoutGreenpool().map(id => Number(id))
    const policiesForPayrollDeduction = policyList.filter(
      p => !p.isGrossSalaryScheme && policyIds.includes(p.id)
    )
    const samePaymentInterval = policiesForPayrollDeduction?.find(
      p => p.paymentInterval > 1
    )?.paymentInterval
    const hasSameInterval = policiesForPayrollDeduction?.every(
      p => p.paymentInterval === samePaymentInterval
    )
    if (hasSameInterval && assetTotal === 0) {
      return samePaymentInterval
    }
    const hasDefaultInterval = policiesForPayrollDeduction?.every(
      p => p.paymentInterval === 0 || p.paymentInterval === 1
    )
    if (hasDefaultInterval) {
      return 'default'
    }
    return 'varied'
  }

  const calculateTotal = ({
    interval,
    policyList,
    invalidProductPrices,
  }: {
    interval: number | string | undefined
    policyList: PolicyCoreData[]
    invalidProductPrices?: Record<number, { oldPrice: number; newPrice: number }>
  }) => {
    const nonMandatoryItemsGrouped = Object.fromEntries(
      Object.entries(getNonMandatoryItemsGroupedByPolicy()).filter(([, items]) =>
        items.every(item => !item.isGreenpoolProduct)
      )
    )
    let totalDeduction = 0
    let grossSalary: MonthlyPriceByInterval[] = []
    let totalByInterval: MonthlyPriceByInterval[] = []
    let newInterval: number | string | undefined = interval
    let pricePerPolicy: Record<
      number,
      { payrollDeduction: number; paymentInterval: number; isMinAmount: boolean }
    > = {}

    Object.entries(nonMandatoryItemsGrouped).forEach(([policyId, items]) => {
      const policy = policyList.find(p => p.id === Number(policyId))
      if (!policy) return

      const orderTotalCost = items.reduce((acc, item) => {
        if (item.product?.isMandatory) {
          return acc
        }
        const newPrice = invalidProductPrices?.[Number(item.productId)]?.newPrice
        const latestPrice = newPrice || item.product?.price || 0

        return acc + latestPrice * item.qty
      }, 0)

      const coverAvailable = policy.coverAmount - policy.coverUsed
      const coveredAmount = Math.min(orderTotalCost, coverAvailable)
      const amountToPay = Math.max(0, orderTotalCost - coveredAmount)
      const isMinAmount = amountToPay <= policy.minimumAmount && amountToPay > 0

      if (policy.isGrossSalaryScheme) {
        const grossSalaryMonthlyPrice = getPricePerInstallment(amountToPay, policy.paymentInterval)

        if (grossSalaryMonthlyPrice > 0) {
          grossSalary.push({
            monthlyPrice: grossSalaryMonthlyPrice,
            paymentInterval: policy.paymentInterval,
          })
        }
      } else {
        totalDeduction += amountToPay
        pricePerPolicy[Number(policyId)] = {
          payrollDeduction: amountToPay,
          paymentInterval: isMinAmount ? 1 : policy.paymentInterval,
          isMinAmount,
        }
      }
    })

    const hasMinAmount = Object.values(pricePerPolicy).some(p => p.isMinAmount)
    if (hasMinAmount) {
      newInterval = typeof interval === 'number' ? 'varied' : interval
    }

    if (newInterval === 'varied') {
      totalByInterval = Object.values(pricePerPolicy).reduce<MonthlyPriceByInterval[]>(
        (acc, { payrollDeduction, paymentInterval }) => {
          const existing = acc.find(item => item.paymentInterval === paymentInterval)
          if (existing) {
            return acc.map(item =>
              item.paymentInterval === paymentInterval
                ? { ...item, monthlyPrice: item.monthlyPrice + payrollDeduction }
                : item
            )
          }
          return [...acc, { monthlyPrice: payrollDeduction, paymentInterval }]
        },
        []
      )
    }

    return { totalDeduction, grossSalary, totalByInterval, newInterval }
  }

  const handleDeductionByVariedIntervalCase = (
    totalDeductionByInterval: MonthlyPriceByInterval[],
    assetTotal: number
  ) => {
    const firstMonthDeduction = totalDeductionByInterval.filter(
      t => t.paymentInterval === 0 || t.paymentInterval === 1
    )
    const otherMonthsDeduction = totalDeductionByInterval.filter(t => t.paymentInterval > 1)
    let nextMonthPayment = assetTotal
    if (firstMonthDeduction.length > 0) {
      // Handle the case where there is a deduction in the first month
      nextMonthPayment += firstMonthDeduction.reduce((acc, curr) => acc + curr.monthlyPrice, 0)
    }
    // 'asset total' case for varied interval -- when only there is no payroll deduction next month
    if (nextMonthPayment > 0) {
      otherMonthsDeduction.push({
        monthlyPrice: nextMonthPayment,
        paymentInterval: 1,
      })
    }

    return otherMonthsDeduction.sort((a, b) => a.paymentInterval - b.paymentInterval)
  }

  return {
    countAssetsTotal,
    getPayrollDeductionCase,
    calculateTotal,
    handleDeductionByVariedIntervalCase,
  }
}
